/*
 * Created Date: 24-01-2024, 4:07:59 pm
 * Author: Victor
 * Email: victor@pt-corp.vn
 * -----
 * Last Modified: Wed Mar 20 2024
 * Modified By: Victor
 * -----
 * Copyright (c) 2024 PT CORP, Inc
 * -----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	----------------------------------------------------------
 */

import { errorMsg } from '@Core/Helpers/Message'
import { getMinioUrl, getS3Url } from '@Core/Helpers/Url'
import { Delete, UploadFile } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
	Button,
	CircularProgress,
	FormHelperText,
	FormLabel,
	IconButton,
	InputLabel,
	Tooltip,
	Typography
} from '@mui/material'
import { useRequest } from 'ahooks'
import clsx from 'clsx'
import Image from 'mui-image'
import React, { useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MdOutlineBrokenImage } from 'react-icons/md'
import PropTypes from 'prop-types'

const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif']
const msgImage = () => {
	const types = allowedImageTypes.reduce((result, type) => {
		const splitType = type.split('/')[1]

		return `${result}${result?.trim().length === 0 ? splitType : `, ${splitType}`}`
	}, '')
	return {
		error: `Only accept type ${types}`,
		helperText: maxSize => `Accept ${types} and max size is ${maxSize / (1024 * 1024)}MB`
	}
}
const CoreUploadFile = ({
	boxClassName,
	labelClassname,
	imageClassName,
	buttonClassName,
	helperTextClassName,
	uploadService,
	buttonText,
	label,
	control,
	name,
	defaultImage,
	helperText,
	customRender,
	type,
	fielType,
	maxSize,
	required,
	variant = 'default'
}) => {
	const { t } = useTranslation()

	const {
		field: { value, onBlur, onChange, ref },
		fieldState: { error }
	} = useController({ control, name })
	const refInput = useRef(null)

	const { loading: loadingUpload, run: handleUpload } = useRequest(uploadService, {
		manual: true,
		onSuccess: data => {
			onChange(data?.file)
		},
		onError: error => {
			errorMsg(error)
		}
	})

	const handleChangeFile = e => {
		const file = e.target?.files?.[0]
		if (file && file?.size < maxSize) {
			const formValue = new FormData()
			formValue.append('file', file)
			formValue.append('type', type)
			handleUpload(formValue)
		} else {
			errorMsg(t('common:message.max_size'))
		}
	}

	const renderLabel = () => {
		return label ? (
			<InputLabel
				className={clsx('text-[1rem] md:text-12 font-medium text-label', labelClassname)}
				required={required}
				shrink
				htmlFor={name}
			>
				{label || t('Upload')}
			</InputLabel>
		) : null
	}

	const { getRootProps, getInputProps } = useDropzone({
		accept: 'image/*',
		onDrop: async acceptedFiles => {
			if (acceptedFiles[0] && acceptedFiles[0]?.size < 1048576) {
				try {
					if (acceptedFiles[0] && acceptedFiles[0]?.size < maxSize) {
						const formValue = new FormData()
						formValue.append('file', acceptedFiles[0])
						formValue.append('type', type)
						handleUpload(formValue)
					} else {
						errorMsg(t('common:message.max_size'))
					}
				} catch (error) {
					errorMsg(error?.message)
				}
			} else {
				errorMsg(t('common:message.max_size'))
			}
		}
	})

	const renderImage = () => {
		return (
			<div
				{...getRootProps({ className: 'dropzone' })}
				className={clsx(
					'border-dashed  cursor-pointer border-2  overflow-hidden',
					{
						'aspect-square rounded-full': variant === 'default',
						'aspect-video': variant === 'square'
					},
					imageClassName
				)}
			>
				{loadingUpload ? (
					<div className="flex flex-col items-center justify-center h-full">
						<CircularProgress />
					</div>
				) : value ? (
					<Image
						// showLoading
						errorIcon={
							<div className="flex flex-col items-center justify-center">
								<MdOutlineBrokenImage style={{ fontSize: 40 }} />
								<Typography variant="M14" color="error">
									{t('Lỗi hiển thị')}
								</Typography>
							</div>
						}
						src={
							value?.trim().length > 0
								? getS3Url(value)
								: defaultImage || '/assets/images/auth/default_avatar_workspace.png'
						}
						alt="upload file"
					/>
				) : (
					<div className="flex flex-col items-center justify-center h-full">
						<div>
							<UploadFile style={{ fontSize: 40 }} />
						</div>
						<div className="text-center mt-2 text-[0.875rem]">{t('Tải lên')}</div>
					</div>
				)}
			</div>
		)
	}

	const renderButton = () => {
		return (
			<LoadingButton
				onClick={() => refInput.current.click()}
				className={clsx(buttonClassName)}
				loading={loadingUpload}
				variant="secondary-gradient"
			>
				{buttonText || t('Upload')}
			</LoadingButton>
		)
	}

	const renderHelperText = () => {
		const validateText = fielType === 'image' ? msgImage().helperText(maxSize) : ''

		return helperText ? (
			<FormHelperText className={clsx('text-[0.875rem]', helperTextClassName)}>{helperText}</FormHelperText>
		) : null
	}

	return (
		<>
			<input type="file" className="hidden" {...getInputProps()} onChange={handleChangeFile} />
			<input type="file" className="hidden" ref={refInput} onChange={handleChangeFile} />
			{/* // FOR custom position UI for each element */}
			{customRender ? (
				customRender({
					renderLabel,
					renderImage,
					renderButton,
					renderHelperText
				})
			) : (
				<div className={clsx('flex flex-col gap-2  group', boxClassName)}>
					{renderLabel()}
					<div>
						<div className="relative">
							{renderImage()}
							{value && (
								<div
									className={clsx('absolute  hidden group-hover:block z-99', {
										'top-0 left-[5px]': variant === 'default',
										'bottom-0': variant !== 'default'
									})}
								>
									<Tooltip title={t('Xoá ảnh')}>
										<IconButton
											size="small"
											// variant="contained"
											color="error"
											// disableElevation
											onClick={() => onChange(null)}
										>
											<Delete />
										</IconButton>
									</Tooltip>
								</div>
							)}
						</div>
						{renderHelperText()}
					</div>

					{error && error.message && <FormHelperText error={!!error}>{error.message}</FormHelperText>}
				</div>
			)}
		</>
	)
}

CoreUploadFile.defaultProps = {
	maxSize: 1048576
}

CoreUploadFile.propTypes = {
	variant: PropTypes.oneOf(['default', 'square'])
}

export default React.memo(CoreUploadFile)
